import { Stack } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  BidV2Status,
  type BuyerBidV2DetailDto,
} from "../../../../autogen/bff-api";
import { FiShield } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { parseCriterionKey } from "../../../../common/qualification/criterionKey";
import { BidActionsCard } from "./BidActionsCard";
import { BidActivityTimeline } from "./BidActivityTimeline";
import { BidContactCard } from "./BidContactCard";
import type { BidEvaluationSummary } from "./bidEvaluationSummary";
import { effectiveMinimumRequirementVerdict, getBidMinimumRequirementRows } from "./bidMinimumRequirements";
import { getBidQualificationRows } from "./bidQualification";
import { BidRejectionActionCard } from "./BidRejectionActionCard";
import { QualificationsReviewProgressCard } from "./QualificationsReviewProgressCard";

interface Props {
  detail: BuyerBidV2DetailDto;
  summary: BidEvaluationSummary;
  awarded: boolean;
  onMarkWinner: () => void;
  canSelectWinner: boolean;
  onOpenRejectionDetails?: () => void;
}

export const BidDetailSidebar = ({
  detail,
  summary,
  awarded,
  onMarkWinner,
  canSelectWinner,
  onOpenRejectionDetails,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const minimumRequirementRows = getBidMinimumRequirementRows(detail);
  return (
    <Stack spacing="4">
      {detail.bid.status === BidV2Status.Rejected ? (
        <BidRejectionActionCard
          detail={detail}
          onViewRejectionDetails={onOpenRejectionDetails}
        />
      ) : (
        <BidActionsCard
          detail={detail}
          summary={summary}
          awarded={awarded}
          onMarkWinner={onMarkWinner}
          canSelectWinner={canSelectWinner}
        />
      )}
      <BidContactCard detail={detail} />
      <QualificationsReviewProgressCard
        items={getBidQualificationRows(detail).map((r) => ({
          name: parseCriterionKey(r.criterionKey).name,
          verdict: r.buyerVerdict ?? null,
        }))}
        onSeeAll={() => navigate(`?tab=qualifications`, { replace: false })}
      />
      {minimumRequirementRows.length > 0 && (
        <QualificationsReviewProgressCard
          title={String(t("Minimum requirements"))}
          icon={FiShield}
          items={minimumRequirementRows.map((r) => ({
            name: r.requirement.title,
            verdict: effectiveMinimumRequirementVerdict(r.requirement.responseType, r.response),
          }))}
          onSeeAll={() => navigate(`?tab=minimumRequirements`, { replace: false })}
        />
      )}
      <BidActivityTimeline detail={detail} />
    </Stack>
  );
};
